// src/utils/iChing/liuyao.js [2025-11-21 20:30:00]
// 功能：六爻排盘核心计算逻辑 - 完善版
// 目的：实现完整的六爻排盘计算，包括本卦、变卦、六神、伏神及所有相关信息

// ====== 导入历法计算模块 ======
import  {calculateCalendarInfo,getJieQiInfo, getKongWangByGanZhi}  from "./_calendar.jsx";
// ====== 导入六十四卦数据模块 ======
import { getGuaByCode, calculateChangedLiuQin, calculateLiuShen, calculateFuShen } from "./_helper_64gua.jsx";

// ====== 五行映射与配色（提前声明，避免 “Cannot access ... before initialization”） ======
const stemToElement = {
  甲: "wood", 乙: "wood",
  丙: "fire", 丁: "fire",
  戊: "earth", 己: "earth",
  庚: "metal", 辛: "metal",
  壬: "water", 癸: "water"
};

const placeToElement = {
  乾: "metal", 巽: "wood",
  坎: "water", 离: "fire",
  艮: "earth", 坤: "earth",
  震: "wood", 兑: "metal"
};

const liushenToElement = {
  玄武: "water", 朱雀: "fire",
  白虎: "metal", 腾蛇: "earth",
  勾陈: "earth", 青龙: "wood"
};

const branchToElement = {
  子: "water", 丑: "earth", 寅: "wood", 卯: "wood",
  辰: "earth", 巳: "fire", 午: "fire", 未: "earth",
  申: "metal", 酉: "metal", 戌: "earth", 亥: "water"
};

const elementColor = {
  wood: "#16a34a",
  fire: "#ef4444",
  earth: "#92400e", // 备注：土改为暗土色
  metal: "#f59e0b", // 备注：金改为亮金色
  water: "#2563eb",
  gray: "#9ca3af" // 备注：新增淡灰色分类，供天干统一使用（不再区分五行）
};

// 模块级颜色开关（不要在模块顶层使用 React hook）
let showColors = true;
export function setShowColors(flag) { showColors = !!flag; }

// 颜色包裹工具
export function colorizeHtml (text, color) {
  if (!showColors || !color) return String(text || "");
  return `<span style="color:${color}">${String(text || "")}</span>`;
}

// 五行颜色获取（支持卦宫 / 六神 优先匹配；可指定按天干/地支上色）
export function getWuXingColor(ganzhi, part = "auto") {
  if (!ganzhi) return "#374151";
  const s = String(ganzhi).trim();

  // 优先：卦宫、六神 精确映射
  if (placeToElement[s]) return elementColor[placeToElement[s]] || "#374151";
  if (liushenToElement[s]) return elementColor[liushenToElement[s]] || "#374151";

  const colorByStemChar = (str) => {
    const ch = String(str).charAt(0) || "";
    // 备注：天干统一淡灰色，不再按五行区分
    return stemToElement[ch] ? elementColor.gray : null;
  };

  const colorByBranchChar = (str) => {
    const txt = String(str);
    const ch = txt.length >= 2 ? txt.charAt(1) : txt.charAt(0);
    const el = branchToElement[ch];
    return el ? elementColor[el] : null;
  };

  if (part === "stem") return colorByStemChar(s) || "#374151";
  if (part === "branch") return colorByBranchChar(s) || "#374151";

  // auto: 优先天干（淡灰色）、其次地支（五行色）
  const stemColor = colorByStemChar(s);
  if (stemColor) return stemColor;
  const branchColor = colorByBranchChar(s);
  return branchColor || "#374151";
}

// helper: 渲染柱（把天干与地支分别渲染并上色）
export function renderPillar (ganzhi) {
  if (!ganzhi || ganzhi === "—") return <span className="text-xl font-semibold text-gray-800">—</span>;
  const s = String(ganzhi);
  const stem = s.charAt(0) || "";
  const branch = s.charAt(1) || s.slice(1) || "";
  return (
    <span className="text-xl font-semibold">
      <span style={{ color: getWuXingColor(stem, "stem") }}>{stem}</span>
      {/* 缩小天干与地支之间的间距为一个空格（约 0.25rem） */}
      <span style={{ color: getWuXingColor(branch, "branch"), marginLeft: "0.25rem" }}>{branch}</span>
    </span>
  );
};

// helper: 渲染爻线（1 -> 实线，0 -> 虚断线）
export function renderYaoLine(val) {
  const sVal = String(val);
  
  // 1. 特殊符号直接显示
  if (["叉", "圈", "×", "○"].includes(sVal)) {
    return <span className="text-xl font-bold">{sVal}</span>;
  }

  // 2. 实线 (1)
  if (sVal === "1") {
    return <span className="text-xl font-bold text-gray-900">｜</span>;
  }

  // 3. 断线 (0)
  if (sVal === "0") {
    return <span className="text-xl font-bold text-gray-900">||</span>;
  }

  // 4. 其他情况直接显示文本 (如 "—")
  return <span className="text-gray-400">{sVal}</span>;
};

// 将纳甲（如 "甲子"）按天干/地支分别上色，返回 HTML 字符串
export function getNaJiaWithColor(text) {
  if (!text && text !== 0) return "";
  const s = String(text).trim();
  if (s.length >= 2) {
    const stem = s.charAt(0);
    const branch = s.slice(1);
    const stemHtml = colorizeHtml(stem, getWuXingColor(stem, "stem"));
    const branchHtml = colorizeHtml(branch, getWuXingColor(branch, "branch"));
    return `${stemHtml}${branchHtml}`;
  }
  return colorizeHtml(s, getWuXingColor(s, "auto"));
}

// 五行生克关系定义
const elementRelations = {
  wood: { generates: "fire", controls: "earth" },
  fire: { generates: "earth", controls: "metal" },
  earth: { generates: "metal", controls: "water" },
  metal: { generates: "water", controls: "wood" },
  water: { generates: "wood", controls: "fire" }
};

// 天干五合
const stemCombinations = {
  甲: "己", 己: "甲",
  乙: "庚", 庚: "乙",
  丙: "辛", 辛: "丙",
  丁: "壬", 壬: "丁",
  戊: "癸", 癸: "戊"
};

// 地支六合
const branchCombinations = {
  子: "丑", 丑: "子",
  寅: "亥", 亥: "寅",
  卯: "戌", 戌: "卯",
  辰: "酉", 酉: "辰",
  巳: "申", 申: "巳",
  午: "未", 未: "午"
};

// 地支六冲
const branchClashes = {
  子: "午", 午: "子",
  丑: "未", 未: "丑",
  寅: "申", 申: "寅",
  卯: "酉", 酉: "卯",
  辰: "戌", 戌: "辰",
  巳: "亥", 亥: "巳"
};

// 十二长生表 (水土共长生)
const twelveStages = {
  wood: { mu: "未", jue: "申" },
  fire: { mu: "戌", jue: "亥" },
  metal: { mu: "丑", jue: "寅" },
  water: { mu: "辰", jue: "巳" },
  earth: { mu: "辰", jue: "巳" }
};

/**
 * 计算五行旺相休囚死状态
 * @param {string} me - 我的五行
 * @param {string} ref - 参考五行（如月令、日建）
 * @returns {string} 状态字符 (旺/相/休/囚/死)
 */
function getStatus(me, ref) {
  if (!me || !ref) return "";
  if (me === ref) return "旺";
  if (elementRelations[ref].generates === me) return "相";
  if (elementRelations[me].generates === ref) return "休";
  if (elementRelations[me].controls === ref) return "囚";
  if (elementRelations[ref].controls === me) return "死";
  return "";
}

/**
 * 计算纳甲地支在日柱和月柱的旺相休囚状态
 * @param {string} dayPillar - 日柱 (如 "甲子")
 * @param {string} monthPillar - 月柱 (如 "乙丑")
 * @param {string[]} naJiaList - 纳甲数组 (如 ["甲子", ...])
 * @returns {object[]} 包含每一爻旺衰状态的数组 [{ month, day }, ...]
 */
export function calculateWangXiang(dayPillar, monthPillar, naJiaList) {
  // 提取地支的辅助函数
  const getBranch = (str) => {
    if (!str || str === "—") return "";
    const s = String(str).trim();
    return s.length >= 2 ? s.charAt(1) : s;
  };

  const dayBranch = getBranch(dayPillar);
  const monthBranch = getBranch(monthPillar);
  
  const dayEl = branchToElement[dayBranch];
  const monthEl = branchToElement[monthBranch];

  // 确保输入是数组
  const list = Array.isArray(naJiaList) ? naJiaList : [naJiaList];

  return list.map((naJia) => {
    const lineBranch = getBranch(naJia);
    const lineEl = branchToElement[lineBranch];
    
    if (!lineBranch) return { month: "", day: "" };

    // 1. 月建关系：旺相休囚死
    const monthStatus = getStatus(lineEl, monthEl);

    // 2. 日建关系：生克合冲扶
    let dayStatus = "";
    
    // 优先级：冲 > 合 > 扶 > 生 > 克
    if (branchClashes[lineBranch] === dayBranch) {
      dayStatus = "冲";
    } else if (branchCombinations[lineBranch] === dayBranch) {
      dayStatus = "合";
    } else if (lineEl === dayEl) {
      dayStatus = "扶";
    } else if (elementRelations[dayEl] && elementRelations[dayEl].generates === lineEl) {
      dayStatus = "生";
    } else if (elementRelations[dayEl] && elementRelations[dayEl].controls === lineEl) {
      dayStatus = "克";
    } else {
      dayStatus = getStatus(lineEl, dayEl);
    }

    return {
      month: monthStatus,
      day: dayStatus
    };
  });
}

/**
 * 计算本卦与变卦地支之间的关系 (冲、合、生、克)
 * @param {string[]} originalNaJiaList - 本卦纳甲数组
 * @param {string[]} transformedNaJiaList - 变卦纳甲数组
 * @returns {string[]} 关系数组 ["冲", "合", "生", "克", "　", ...]
 */
export function calculateTransformRelation(originalNaJiaList, transformedNaJiaList) {
  const getBranch = (str) => {
    if (!str || str === "—") return "";
    const s = String(str).trim();
    return s.length >= 2 ? s.charAt(1) : s;
  };

  const oriList = Array.isArray(originalNaJiaList) ? originalNaJiaList : [originalNaJiaList];
  const transList = Array.isArray(transformedNaJiaList) ? transformedNaJiaList : [];

  return oriList.map((oriNaJia, index) => {
    const transNaJia = transList[index];
    
    // 如果没有变爻，或者纳甲相同（未变），则无关系，返回全角空格占位
    if (!transNaJia || transNaJia === "—" || transNaJia === oriNaJia) {
      return "　";
    }

    const lineBranch = getBranch(oriNaJia);
    const transBranch = getBranch(transNaJia);
    
    if (!lineBranch || !transBranch) return "　";

    const lineEl = branchToElement[lineBranch];
    const transEl = branchToElement[transBranch];

    let relation = "　";

    // 优先级：冲 > 合 > 生 > 克
    if (branchClashes[lineBranch] === transBranch) {
      relation = "冲";
    } else if (branchCombinations[lineBranch] === transBranch) {
      relation = "合";
    } else if (transEl && lineEl) {
      // 变爻对本爻的作用
      if (elementRelations[transEl].generates === lineEl) {
        relation = "生";
      } else if (elementRelations[transEl].controls === lineEl) {
        relation = "克";
      }
    }
    
    return relation;
  });
}

/**
 * 计算变爻的状态（绝、墓、空亡）
 * @param {string[]} originalNaJiaList - 本卦纳甲数组
 * @param {string[]} transformedNaJiaList - 变卦纳甲数组
 * @param {string} dayPillar - 日柱
 * @returns {string[]} 状态数组
 */
export function calOriatTraState(originalNaJiaList, transformedNaJiaList, dayPillar) {
  const getBranch = (str) => {
    if (!str || str === "—") return "";
    const s = String(str).trim();
    return s.length >= 2 ? s.charAt(1) : s;
  };

  const oriList = Array.isArray(originalNaJiaList) ? originalNaJiaList : [originalNaJiaList];
  const transList = Array.isArray(transformedNaJiaList) ? transformedNaJiaList : [];
  
  // 获取日柱空亡
  const kwInfo = getKongWangByGanZhi(dayPillar);
  const kongWangStr = kwInfo ? kwInfo.kongWang : "";

  return oriList.map((oriNaJia, index) => {
    const transNaJia = transList[index];
    
    // 如果没有变爻，或者纳甲相同，则不显示化绝/化墓/化空
    if (!transNaJia || transNaJia === "—" || transNaJia === oriNaJia) {
      return "　"; 
    }

    const oriBranch = getBranch(oriNaJia);
    const transBranch = getBranch(transNaJia);
    
    if (!oriBranch || !transBranch) return "　";

    const oriEl = branchToElement[oriBranch];
    
    let res = "";
    
    // 1. 绝/墓
    if (oriEl && twelveStages[oriEl]) {
      if (twelveStages[oriEl].mu === transBranch) res += "墓";
      if (twelveStages[oriEl].jue === transBranch) res += "绝";
    }

    // 2. 空亡
    if (kongWangStr.includes(transBranch)) {
      res += "空";
    }

    return res || "　";
  });
}


/**
 * 将奇数个数列表转换为二进制爻象
 * @param {number[]} divinationNumbers - 奇数个数列表，如 [0, 1, 3, 1, 1, 2]
 * @returns {string} 六位二进制字符串，如 "011110"
 */
function convertToBinary(divinationNumbers) {
  return divinationNumbers.map(num => num % 2 === 1 ? '1' : '0').join('');
}

/**
 * 根据二进制爻象获取爻的类型和名称
 * @param {string} binaryYao - 二进制爻象，'0' 或 '1'
 * @param {number} originalValue - 原始值，0-3
 * @returns {object} 爻的信息 {type, name, isChanging}
 */
function getYaoInfo(binaryYao, originalValue) {
  let type, name, isChanging = false;

  if (binaryYao === '1') {
    if (originalValue === 3) { // 老阳
      type = '老阳';
      name = '老阳 ⚊ (变爻)';
      isChanging = true;
    } else { // 少阳
      type = '少阳';
      name = '少阳 ⚊';
    }
  } else { // binaryYao === '0'
    if (originalValue === 0) { // 老阴
      type = '老阴';
      name = '老阴 ⚋ (变爻)';
      isChanging = true;
    } else { // 少阴
      type = '少阴';
      name = '少阴 ⚋';
    }
  }

  return { type, name, isChanging };
}

/**
 * 根据二进制爻象计算变卦的二进制表示
 * @param {string} originalBinary - 原始卦的二进制表示
 * @param {number[]} originalValues - 原始值列表，0-3
 * @returns {string} 变卦的二进制表示
 */
function calculateTransformedBinary(originalBinary, originalValues) {
  let transformedBinary = '';

  for (let i = 0; i < originalValues.length; i++) {
    const originalValue = originalValues[i];

    if (originalValue === 0) { // 老阴变少阳
      transformedBinary += '1';
    } else if (originalValue === 3) { // 老阳变少阴
      transformedBinary += '0';
    } else { // 少阳、少阴不变
      transformedBinary += originalBinary[i];
    }
  }

  return transformedBinary;
}

/**
 * 六爻排盘核心计算函数
 * @param {string} datetimeStr - 公历时间字符串，格式 "YYYYMMDDHHmmss"
 * @param {boolean} splitZiHour - 是否使用早晚子时
 * @param {number[]} divinationNumbers - 起卦数字列表，例如 [0, 1, 3, 1, 1, 2]
 * @returns {object} 包含排盘结果的对象
 */
export function calculateLiuYao(datetimeStr, splitZiHour, divinationNumbers) {
  console.log("calculateLiuYao 函数被成功调用了！");
  console.log("接收到的参数:", { datetimeStr, splitZiHour, divinationNumbers });

  // --- 1. 计算时间信息（调用独立的历法模块）---
  const dayZhiType = splitZiHour ? 2 : 1;
  const dateInfo = calculateCalendarInfo(datetimeStr, splitZiHour, dayZhiType);

  // --- 2. 将奇数个数列表转换为二进制爻象 ---
  const originalBinary = convertToBinary(divinationNumbers);
  console.log("本卦二进制:", originalBinary);

  // --- 3. 查找本卦信息 ---
  const originalGua = getGuaByCode(originalBinary);
  if (!originalGua) {
    throw new Error(`无法找到二进制码 ${originalBinary} 对应的卦象`);
  }
  console.log("本卦信息:", originalGua);

  // --- 4. 计算变爻和变卦 ---
  // 获取每个爻的详细信息
  const yaoDetails = divinationNumbers.map((value, index) => {
    const binaryYao = originalBinary[index];
    return getYaoInfo(binaryYao, value);
  });

  // 找出所有变爻的位置（从下往上，1-6）
  const changingYaoPositions = [];
  yaoDetails.forEach((yao, index) => {
    if (yao.isChanging) {
      changingYaoPositions.push(index + 1); // 转换为1-6的表示
    }
  });

  // 计算变卦的二进制表示
  const transformedBinary = calculateTransformedBinary(originalBinary, divinationNumbers);
  console.log("变卦二进制:", transformedBinary);

  // 查找变卦信息
  const transformedGua = getGuaByCode(transformedBinary);
  if (!transformedGua) {
    throw new Error(`无法找到二进制码 ${transformedBinary} 对应的卦象`);
  }
  console.log("变卦信息:", transformedGua);

  // --- 5. 计算变卦的六亲 ---
  // 获取本卦的宫位
  const originalPalace = originalGua.palace;

  // 获取变卦的地支列表
  const transformedBranches = transformedGua.branches;

  // 计算变卦的六亲
  const transformedLiuQin = calculateChangedLiuQin(originalPalace, transformedBranches);

  // --- 6. 计算六神 ---
  // 获取日干（从日干支中提取天干部分）
  const dayGan = dateInfo.ganzhi.day.substring(0, 1);

  // 计算本卦的六神
  const originalLiuShen = calculateLiuShen(dayGan);

  // 计算变卦的六神
  const transformedLiuShen = calculateLiuShen(dayGan);

  // --- 7. 计算伏神 ---
  // 计算本卦的伏神
  const originalFuShen = calculateFuShen(originalPalace, originalGua.liu_qin, originalGua.branches);

  // --- 8. 构建返回结果 ---
  const result = {
    success: true,
    date_info: dateInfo,
    divination_params: {
      method: "manual_input",
      numbers: divinationNumbers,
      binary_code: originalBinary,
      yao_details: yaoDetails
    },
    original_hexagram: {
      name: originalGua.name,
      code: originalGua.code,
      symbol: originalGua.symbol || "",
      nature: originalGua.nature || "",
      attribute: originalGua.attribute || "",
      gua_ci: originalGua.gua_ci || "",
      palace: originalGua.palace,
      upper_gua: originalGua.upper,
      lower_gua: originalGua.lower,
      shi_yao: originalGua.shi_yao,
      shi_type: originalGua.shi_type,
      stems: originalGua.stems,
      branches: originalGua.branches,
      liu_qin: originalGua.liu_qin,
      liu_shen: originalLiuShen,
      fu_shen: originalFuShen  // 【新增】添加本卦伏神
    },
    transformed_hexagram: {
      name: transformedGua.name,
      code: transformedGua.code,
      symbol: transformedGua.symbol || "",
      nature: transformedGua.nature || "",
      attribute: transformedGua.attribute || "",
      gua_ci: transformedGua.gua_ci || "",
      palace: transformedGua.palace,
      upper_gua: transformedGua.upper,
      lower_gua: transformedGua.lower,
      shi_yao: transformedGua.shi_yao,
      shi_type: transformedGua.shi_type,
      stems: transformedGua.stems,
      branches: transformedGua.branches,
      liu_qin: transformedLiuQin,
      liu_shen: transformedLiuShen
      // 注意：变卦通常不需要计算伏神
    },
    changing_yao: {
      positions: changingYaoPositions,
      count: changingYaoPositions.length
    },
    // 调试信息
    debug: {
      original_binary: originalBinary,
      transformed_binary: transformedBinary,
      yao_details: yaoDetails
    }
  };

  console.log("排盘计算结果:", result);
  return result;
}